import { z } from 'zod';
import {
  MAX_BREAKS,
  ShiftSchema,
  isApproved,
  isShiftOpen,
  needsApproval,
  runningBreak,
  shiftsOverlap,
  type Shift,
  type ShiftBreak,
} from '../../domain/entities/shift.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged, updateWrite } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';

/**
 * The time clock (SCRUM-142, P8-07).
 *
 * **Everything here is about one person being in one place at a time.**
 * The refusals are the feature: a second open shift, a break that outlives
 * its shift, a clock-out before the clock-in. Each of them produces hours
 * that somebody will be paid on, and a wrong one is found weeks later by
 * an argument rather than by an error.
 */
export class TimeClockError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeClockError';
  }
}

export interface TimeClockContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  /** The person acting. Not necessarily whose shift it is — see `forUid`. */
  readonly uid: string;
}

const scope = (context: TimeClockContext) => ({ companyId: context.companyId });

const shifts = (context: TimeClockContext) =>
  context.data.collection<Shift>(scope(context), 'shifts');

/**
 * The query a crew member is allowed to issue.
 *
 * `timeClock: edit (own)` in the matrix, and the rules enforce it per
 * returned document — so, as with warranty claims and spec sections, the
 * filter **is** the permission: an unfiltered list fails on the first
 * shift that is not theirs.
 */
export function ownShiftQuery(uid: string): Query {
  return {
    where: [{ field: 'uid', op: '==', value: uid }],
    orderBy: [{ field: 'startedAt', direction: 'desc' }],
  };
}

/**
 * Everybody still on the clock.
 *
 * Open shifts are those with **no `endedAt` field at all**, which Firestore
 * cannot query for directly — there is no "field does not exist" filter.
 * So the office's board reads the recent shifts and filters in memory,
 * which is cheap because the set is small and bounded by the day. A
 * stored `open: boolean` would be a second opinion about `endedAt` and
 * the two would part company the first time a shift was edited.
 */
export function openShifts(all: readonly Shift[]): Shift[] {
  return all.filter(isShiftOpen);
}

export async function listOwnShifts(
  context: TimeClockContext,
  uid = context.uid,
): Promise<Shift[]> {
  const page = await shifts(context).list(ownShiftQuery(uid));
  return [...page.items];
}

/**
 * One job's shifts, newest first.
 *
 * Only a reader who may see everybody's time can issue this — the rule is
 * per document, so a crew member filtering by job would be refused on the
 * first shift that is not theirs. The job dashboard gates the panel on
 * the same flag rather than letting the query fail and showing an empty
 * one: "nobody is clocked in" and "you may not know who is" are different
 * statements.
 */
export function jobShiftQuery(jobId: string): Query {
  return {
    where: [{ field: 'jobId', op: '==', value: jobId }],
    orderBy: [{ field: 'startedAt', direction: 'desc' }],
  };
}

export function subscribeToJobShifts(
  context: TimeClockContext,
  jobId: string,
  onChange: (shifts: readonly Shift[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return shifts(context).subscribe(jobShiftQuery(jobId), (page) => onChange(page.items), onError);
}

export async function listCompanyShifts(context: TimeClockContext): Promise<Shift[]> {
  const page = await shifts(context).list({
    orderBy: [{ field: 'startedAt', direction: 'desc' }],
  });
  return [...page.items];
}

export function subscribeToOwnShifts(
  context: TimeClockContext,
  uid: string,
  onChange: (shifts: readonly Shift[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return shifts(context).subscribe(ownShiftQuery(uid), (page) => onChange(page.items), onError);
}

export async function getShift(context: TimeClockContext, id: string): Promise<Shift> {
  const found = await shifts(context).get(id);
  if (!found) throw new NotFoundError('shifts', id);
  return found;
}

export const ClockInSchema = z.object({
  jobId: z.string().min(1, 'Say which job this is'),
  costCodeId: z.string().optional(),
  note: z.string().trim().max(2_000).default(''),
});
export type ClockInInput = z.input<typeof ClockInSchema>;

/**
 * Start a shift.
 *
 * **Refused if the person already has one open**, which is the single most
 * likely thing to go wrong with a time clock: a phone that did not
 * register the clock-out, a second device, a double tap. Two open shifts
 * produce two sets of hours for one afternoon and nobody notices until
 * payroll.
 *
 * The refusal names the job they are already on, because the fix depends
 * on it — clocking out of the right job is a different action from
 * realising you never clocked out of yesterday's.
 */
export async function clockIn(
  context: TimeClockContext,
  input: ClockInInput,
  forUid = context.uid,
): Promise<Shift> {
  const parsed = ClockInSchema.safeParse(input);
  if (!parsed.success) throw new TimeClockError(parsed.error.issues[0]!.message);

  const open = openShifts(await listOwnShifts(context, forUid));
  if (open.length > 0) {
    throw new TimeClockError(
      'You are already clocked in. Clock out of that shift before starting another.',
    );
  }

  const at = context.clock.now().toISOString();
  const shift = ShiftSchema.parse({
    id: newId(),
    companyId: context.companyId,
    jobId: parsed.data.jobId,
    uid: forUid,
    ...(parsed.data.costCodeId ? { costCodeId: parsed.data.costCodeId } : {}),
    startedAt: at,
    breaks: [],
    note: parsed.data.note,
    source: 'clock',
    createdAt: at,
    createdBy: context.uid,
    updatedAt: at,
    updatedBy: context.uid,
    version: 1,
  });
  await shifts(context).create(shift);
  return shift;
}

async function write(
  context: TimeClockContext,
  existing: Shift,
  patch: Partial<Shift>,
): Promise<Shift> {
  const updatedAt = context.clock.now().toISOString();
  await context.data.commit([
    updateWrite<Shift>(
      scope(context),
      'shifts',
      existing.id,
      { ...patch, updatedAt, updatedBy: context.uid },
      expectUnchanged(existing),
    ),
  ]);
  return { ...existing, ...patch, updatedAt, updatedBy: context.uid };
}

/**
 * End a shift.
 *
 * **A running break is ended with it**, rather than refused. Forgetting to
 * come back from lunch and then clocking out is the ordinary way this
 * happens, and refusing would leave somebody with a shift they cannot
 * close and a break still running — which is worse arithmetic than the
 * one this quietly corrects, and it corrects it in the direction that does
 * not pay for the break.
 */
export async function clockOut(context: TimeClockContext, shift: Shift): Promise<Shift> {
  if (!isShiftOpen(shift)) {
    throw new TimeClockError('That shift is already closed.');
  }
  const at = context.clock.now().toISOString();
  const running = runningBreak(shift);
  return write(context, shift, {
    endedAt: at,
    ...(running
      ? {
          breaks: shift.breaks.map((one) =>
            one.id === running.id ? { ...one, endedAt: at } : one,
          ),
        }
      : {}),
  });
}

/** Start a break. */
export async function startBreak(context: TimeClockContext, shift: Shift): Promise<Shift> {
  if (!isShiftOpen(shift)) throw new TimeClockError('That shift is closed.');
  if (runningBreak(shift)) throw new TimeClockError('You are already on a break.');
  if (shift.breaks.length >= MAX_BREAKS) {
    throw new TimeClockError(`A shift holds at most ${MAX_BREAKS} breaks.`);
  }
  const breakRecord: ShiftBreak = { id: newId(), startedAt: context.clock.now().toISOString() };
  return write(context, shift, { breaks: [...shift.breaks, breakRecord] });
}

export async function endBreak(context: TimeClockContext, shift: Shift): Promise<Shift> {
  const running = runningBreak(shift);
  if (!running) throw new TimeClockError('You are not on a break.');
  const at = context.clock.now().toISOString();
  return write(context, shift, {
    breaks: shift.breaks.map((one) => (one.id === running.id ? { ...one, endedAt: at } : one)),
  });
}

export const ManualShiftSchema = z.object({
  jobId: z.string().min(1, 'Say which job this is'),
  costCodeId: z.string().optional(),
  startedAt: z.string().min(1, 'Say when it started'),
  endedAt: z.string().min(1, 'Say when it finished'),
  note: z.string().trim().max(2_000).default(''),
});
export type ManualShiftInput = z.input<typeof ManualShiftSchema>;

/**
 * Record a shift that was worked but never clocked.
 *
 * A real and frequent case — a flat battery, a forgotten phone, a day
 * nobody had the app — and refusing it would not stop the hours being
 * claimed, only push them onto a scrap of paper. It is marked
 * `source: 'manual'` so whoever approves the week can see which entries
 * this system witnessed and which it was told about.
 *
 * **Refused if it overlaps a shift the same person already has**, because
 * two sets of hours for one afternoon is the thing this module exists to
 * prevent, and a typed entry is the easiest way to create one.
 */
export async function recordShift(
  context: TimeClockContext,
  input: ManualShiftInput,
  forUid = context.uid,
): Promise<Shift> {
  const parsed = ManualShiftSchema.safeParse(input);
  if (!parsed.success) throw new TimeClockError(parsed.error.issues[0]!.message);
  if (parsed.data.endedAt <= parsed.data.startedAt) {
    throw new TimeClockError('A shift has to finish after it starts.');
  }

  const now = context.clock.now().toISOString();
  await assertNoOverlap(context, forUid, parsed.data, now);

  const shift = ShiftSchema.parse({
    id: newId(),
    companyId: context.companyId,
    jobId: parsed.data.jobId,
    uid: forUid,
    ...(parsed.data.costCodeId ? { costCodeId: parsed.data.costCodeId } : {}),
    startedAt: parsed.data.startedAt,
    endedAt: parsed.data.endedAt,
    breaks: [],
    note: parsed.data.note,
    source: 'manual',
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
    version: 1,
  });
  await shifts(context).create(shift);
  return shift;
}

/**
 * Correct the times on a shift.
 *
 * Leaves `source` alone: a clocked shift whose times were corrected is
 * still a clocked shift, and relabelling it would hide that the system
 * saw it happen. The correction itself is visible in the audit fields.
 */
export async function editShift(
  context: TimeClockContext,
  shift: Shift,
  input: ManualShiftInput,
): Promise<Shift> {
  const parsed = ManualShiftSchema.safeParse(input);
  if (!parsed.success) throw new TimeClockError(parsed.error.issues[0]!.message);
  if (parsed.data.endedAt <= parsed.data.startedAt) {
    throw new TimeClockError('A shift has to finish after it starts.');
  }

  const now = context.clock.now().toISOString();
  await assertNoOverlap(context, shift.uid, parsed.data, now, shift.id);

  return write(context, shift, {
    jobId: parsed.data.jobId,
    ...(parsed.data.costCodeId
      ? { costCodeId: parsed.data.costCodeId }
      : { costCodeId: undefined }),
    startedAt: parsed.data.startedAt,
    endedAt: parsed.data.endedAt,
    note: parsed.data.note,
    /**
     * **Changing the hours takes the approval off.**
     *
     * An approval is a statement about particular numbers. Leaving it in
     * place while the numbers move would make it a statement about
     * whatever they become, which is no statement at all — and the shift
     * would go to payroll carrying a sign-off nobody gave for those
     * hours. The screen says it has gone back onto the list.
     */
    approval: undefined,
  });
}

export async function deleteShift(context: TimeClockContext, shift: Shift): Promise<void> {
  await shifts(context).delete(shift.id);
}

/** Everything finished and not yet signed off, oldest first. */
export function unapprovedShifts(all: readonly Shift[]): Shift[] {
  return (
    all
      .filter(needsApproval)
      // Oldest first, unlike every other list here. A week that has been
      // waiting a fortnight is the one holding up payroll, and sorting it to
      // the bottom is how it carries on waiting.
      .sort((a, b) => a.startedAt.localeCompare(b.startedAt) || a.id.localeCompare(b.id))
  );
}

/**
 * Sign a shift off.
 *
 * **Refused on an open shift.** Approving hours that are still being
 * worked would approve a number that has not stopped moving — and the
 * person would carry on working against an approval already given.
 *
 * Approving one that is already approved is refused rather than treated
 * as a no-op: the second caller is working from a stale screen, and the
 * one thing they should not conclude is that nothing had happened.
 */
export async function approveShift(context: TimeClockContext, shift: Shift): Promise<Shift> {
  if (isShiftOpen(shift)) {
    throw new TimeClockError('That shift is still running. It can be approved once it is closed.');
  }
  if (isApproved(shift)) throw new TimeClockError('That shift is already approved.');
  return write(context, shift, {
    approval: { at: context.clock.now().toISOString(), byUid: context.uid },
  });
}

/**
 * Take an approval back.
 *
 * Before payroll runs, somebody notices. There is no record kept of the
 * withdrawn approval beyond the audit fields, deliberately: a shift
 * carries whether it is approved **now**, and a history of approvals is a
 * change log, which is `changeLog`'s job rather than this document's.
 */
export async function unapproveShift(context: TimeClockContext, shift: Shift): Promise<Shift> {
  if (!isApproved(shift)) throw new TimeClockError('That shift is not approved.');
  return write(context, shift, { approval: undefined });
}

/**
 * Sign off a week in one action.
 *
 * The reason this exists rather than leaving the caller to loop: approving
 * forty shifts one at a time is what makes somebody stop approving them.
 * Each write is guarded by the copy it was read from and they go in one
 * commit, so a week somebody else is editing is refused whole rather than
 * half-approved — which would be worse than not approving at all, because
 * the list would then look done.
 */
export async function approveShifts(
  context: TimeClockContext,
  toApprove: readonly Shift[],
): Promise<Shift[]> {
  const open = toApprove.find(isShiftOpen);
  if (open) {
    throw new TimeClockError('One of those shifts is still running. Close it before approving.');
  }
  const already = toApprove.find(isApproved);
  if (already) throw new TimeClockError('One of those shifts is already approved.');
  if (toApprove.length === 0) return [];

  const at = context.clock.now().toISOString();
  const approval = { at, byUid: context.uid };
  await context.data.commit(
    toApprove.map((shift) =>
      updateWrite<Shift>(
        scope(context),
        'shifts',
        shift.id,
        { approval, updatedAt: at, updatedBy: context.uid },
        expectUnchanged(shift),
      ),
    ),
  );
  return toApprove.map((shift) => ({
    ...shift,
    approval,
    updatedAt: at,
    updatedBy: context.uid,
  }));
}

async function assertNoOverlap(
  context: TimeClockContext,
  forUid: string,
  times: { readonly startedAt: string; readonly endedAt?: string },
  now: string,
  exceptId?: string,
): Promise<void> {
  const existing = await listOwnShifts(context, forUid);
  const clash = existing.find((one) => one.id !== exceptId && shiftsOverlap(one, times, now));
  if (clash) {
    throw new TimeClockError(
      'That overlaps a shift already recorded for this person. Two sets of hours for one afternoon is the thing this is meant to catch.',
    );
  }
}
